/**
 * Shared date formatting utilities for consistent date display across the app.
 */

/**
 * Formats a date to a compact readable string.
 * Output format: "Sun, 1/4, 11:41 AM"
 *
 * Used in: Order grids, delivery windows, updated by tags
 */
export function formatCompactDateTime(
  date: string | Date | null | undefined,
): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '-'

  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' })
  const month = d.getMonth() + 1
  const day = d.getDate()
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  return `${weekday}, ${month}/${day}, ${time}`
}

/**
 * Formats a date range using compact format.
 * Output format: "Mon, 1/12, 6:00 AM - Mon, 1/12, 3:00 PM"
 *
 * Used in: Delivery window columns
 */
export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  if (!start || !end) return null

  const startFormatted = formatCompactDateTime(start)
  const endFormatted = formatCompactDateTime(end)

  if (startFormatted === '-' || endFormatted === '-') return null

  return `${startFormatted} - ${endFormatted}`
}

/**
 * Formats a timestamp as a verbose readable string.
 * Output format: "Jan 13, 2026 at 9:04 AM"
 *
 * Used in: Order history, log timestamps
 */
export function formatTimestamp(
  timestamp: string | Date | null | undefined,
): string {
  if (!timestamp) return ''
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Formats an effective-date value, treating anything past year 3000 as an
 * open-ended/no-expiration sentinel rather than a real date.
 * Output format: "Jan 13, 2026" or "Forever"
 *
 * Effective dates are calendar days stored as midnight UTC, so this reads the
 * UTC date. Converting to local time would show the previous day west of UTC.
 *
 * Used in: Supply Plans effective from/to columns
 */
export function formatEffectiveDate(
  date: string | Date | null | undefined,
): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '-'
  if (d.getUTCFullYear() > 3000) return 'Forever'
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Formats a delivery window date/time into separate parts.
 * Output: { date: "Jan 13, 2026", time: "9:04 AM" }
 *
 * Used in: Delivery window change summaries
 */
export function formatDeliveryTime(dateStr: string): {
  date: string
  time: string
} {
  const date = new Date(dateStr)
  return {
    date: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
  }
}

/**
 * Formats a date as a short human-readable string.
 * Output format: "Jan 13, 2026"
 *
 * Used in: Creation dates, simple date displays
 */
export function formatShortDate(
  date: string | Date | null | undefined,
): string | null {
  if (!date) return null
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return null

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Formats a date as a short month/day plus the time of day, no year.
 * Output format: "Aug 15, 2:18 PM"
 *
 * Used in: issue feeds, where the relative age sits alongside it and the year
 * would be noise.
 */
export function formatShortDateTime(
  date: string | Date | null | undefined,
): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '-'

  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  return `${day}, ${time}`
}

const STALE_AFTER_HOURS = 4
const LATE_AFTER_HOURS = 24

export type StalenessLevel = 'ok' | 'stale' | 'late' | 'none'

/** "just now" / "5m ago" / "10h ago" / "2d ago"; "-" when missing/invalid. */
export function formatRelativeTime(
  date: string | Date | null | undefined,
): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '-'
  const diffMs = Date.now() - d.getTime()
  const minutes = Math.floor(diffMs / 60_000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days > 0) return `${days}d ago`
  if (hours > 0) return `${hours}h ago`
  if (minutes > 0) return `${minutes}m ago`
  return 'just now'
}

/** Freshness bucket for a received-at timestamp. */
export function stalenessLevel(
  receivedAt: string | Date | null | undefined,
): StalenessLevel {
  if (!receivedAt) return 'none'
  const d = typeof receivedAt === 'string' ? new Date(receivedAt) : receivedAt
  if (Number.isNaN(d.getTime())) return 'none'
  const hours = (Date.now() - d.getTime()) / 3_600_000
  if (hours >= LATE_AFTER_HOURS) return 'late'
  if (hours >= STALE_AFTER_HOURS) return 'stale'
  return 'ok'
}
